import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { ArrowLeft, ChevronLeft, ChevronRight, Building2, MapPin, Phone, Users, Edit, Plus, Loader2, X, Mail, MessageSquare, ExternalLink, Check, ChevronsUpDown, Pencil, Save, Globe, FileText, Search, UserCheck, UserX, User, Zap, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatPhone } from '../../utils/formatPhone';
import {
  useCompanyData,
  useLocationEditor,
  useTechStackEditor,
  useWebsiteEditor,
  useLeadManager,
  formatShorthand
} from '../../hooks/useCompanyDetail';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CompanyResearchTab } from '../../components/crm/CompanyResearchTab';
import { CompanyDecisionMakersTab } from '../../components/crm/CompanyDecisionMakersTab';
import CompanyContactStrategy from '../../components/crm/CompanyContactStrategy';
import { ERSBadge } from '../../components/crm/ERSBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Constants
const COUNTRIES = ['USA', 'Canada', 'UK', 'India', 'Australia', 'Germany', 'France', 'Other'];
const ATS_OPTIONS = ['', 'JobDiva', 'Bullhorn', 'Ciepal', 'Salesforce', 'Greenhouse', 'Workday', 'iCIMS', 'Taleo'];
const AI_VOICE_OPTIONS = ['', 'ConverzAI', 'HeyMilo', 'Glider', 'PurpleFish', 'JobTalk', 'Paradox', 'HireVue'];
const COMPANY_TYPE_OPTIONS = [
  { value: 'potential', label: 'Potential' },
  { value: 'client', label: 'Client' },
  { value: 'pot-comp', label: 'Pot-Comp' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'others', label: 'Others' }
];

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'assigned', label: 'Assigned', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-green-100 text-green-700' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-700' },
];

// Searchable Combobox Component
const SearchableCombobox = ({ value, onChange, options, placeholder, testId }) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setOpen(false);
  };

  const handleInputChange = (newValue) => {
    onChange(newValue);
  };

  const filteredOptions = options.filter(opt =>
    opt === '' || opt.toLowerCase().includes((value || '').toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <Input
          value={value || ''}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          className="pr-8 bg-white"
          onClick={() => setOpen(true)}
          onFocus={() => setOpen(true)}
          data-testid={testId}
        />
        <ChevronsUpDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      </div>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput 
            placeholder={`Search ${placeholder}...`} 
            value={value || ''}
            onValueChange={handleInputChange}
          />
          <CommandList>
            <CommandEmpty>
              {value ? (
                <button
                  className="w-full px-2 py-1.5 text-sm text-left hover:bg-slate-100 rounded"
                  onClick={() => handleSelect(value)}
                >
                  Use &ldquo;{value}&rdquo;
                </button>
              ) : (
                'No options found.'
              )}
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option === '' ? '__blank__' : option}
                  value={option === '' ? '__blank__' : option}
                  onSelect={() => handleSelect(option)}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === option ? 'opacity-100' : 'opacity-0'}`} />
                  {option || <span className="text-slate-400 italic">(None)</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const getStatusBadge = (status) => {
  const s = STATUSES.find(x => x.value === status) || STATUSES[0];
  return <Badge className={s.color}>{s.label}</Badge>;
};

const getCommIcon = (type) => {
  switch(type) {
    case 'email': return <Mail className="w-4 h-4 text-indigo-500" />;
    case 'sms': return <MessageSquare className="w-4 h-4 text-green-500" />;
    case 'call': return <Phone className="w-4 h-4 text-orange-500" />;
    default: return null;
  }
};

const getCommBgColor = (type) => {
  switch(type) {
    case 'email': return 'bg-indigo-500';
    case 'sms': return 'bg-green-500';
    case 'call': return 'bg-orange-500';
    default: return 'bg-slate-500';
  }
};

// Quick research trigger button shown in company header
function ResearchQuickButton({ companyId, onResearched }) {
  const [running, setRunning] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    setRunning(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/research`, {}, { withCredentials: true });
      toast.success('Research started — check the Intelligence tab for results');
      // Poll until done and then reload company data
      const poll = async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/research`, { withCredentials: true });
          if (['in_progress', 'queued'].includes(res.data?.research_status)) {
            setTimeout(poll, 4000);
          } else {
            setRunning(false);
            if (onResearched) onResearched();
          }
        } catch { setRunning(false); }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start research');
      setRunning(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-6 text-xs gap-1 border-dashed"
      onClick={handleClick}
      disabled={running}
      title="Run company intelligence research"
    >
      {running
        ? <><RefreshCw className="w-3 h-3 animate-spin" />Researching...</>
        : <><Zap className="w-3 h-3 text-blue-500" />Research</>
      }
    </Button>
  );
}

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // Use custom hooks for state management
  const {
    company,
    loading,
    selectedLocationId,
    loadData,
    handleLocationClick,
    getSelectedLocationName,
    filteredLeads,
    filteredCommunications
  } = useCompanyData(id, navigate);

  // Dynamic tab title — show company name
  useEffect(() => {
    if (company?.name) {
      document.title = `${company.name} | JTCRM`;
    }
    return () => { document.title = 'JTCRM'; };
  }, [company?.name]);

  // Neighbour company IDs for prev/next navigation + search
  const [neighborIds, setNeighborIds] = useState([]);
  const [allCompaniesNav, setAllCompaniesNav] = useState([]);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchOpen, setCompanySearchOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/companies?limit=2000`, { withCredentials: true })
      .then(res => {
        const items = res.data?.items || res.data || [];
        setNeighborIds(items.map(c => c.id).filter(Boolean));
        setAllCompaniesNav(items.map(c => ({ id: c.id, name: c.name })).filter(c => c.id));
      })
      .catch(() => {});
  }, []);
  const currentIdx = neighborIds.indexOf(id);
  const prevId = currentIdx > 0 ? neighborIds[currentIdx - 1] : null;
  const nextId = currentIdx >= 0 && currentIdx < neighborIds.length - 1 ? neighborIds[currentIdx + 1] : null;
  const companySearchResults = companySearchQuery.trim()
    ? allCompaniesNav.filter(c => c.name?.toLowerCase().includes(companySearchQuery.toLowerCase())).slice(0, 8)
    : [];

  const locationEditor = useLocationEditor(company, id, loadData);
  const techStackEditor = useTechStackEditor(company, id, loadData);
  const websiteEditor = useWebsiteEditor(company, id, loadData);
  const leadManager = useLeadManager(id, company?.name, selectedLocationId, loadData);

  // Ownership claim state
  const [claiming, setClaiming] = useState(false);

  // Website hover preview
  const webPreviewTimerRef = useRef(null);
  const [webPreview, setWebPreview] = useState(null);

  const isAdmin = currentUser?.role === 'ADMIN';

  // Row-level access: can this user edit/communicate with this company?
  const canCommunicate = company?.can_communicate ?? true;

  // Admin status toggle state
  const [togglingStatus, setTogglingStatus] = useState(false);

  // Admin owner transfer state
  const [ownerPopoverOpen, setOwnerPopoverOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [transferringOwner, setTransferringOwner] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const handleClaimCompany = async () => {
    setClaiming(true);
    try {
      await axios.put(`${API}/companies/${id}/claim`);
      toast.success('Company claimed — you are now the owner');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim company');
    } finally {
      setClaiming(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = company.status === 'new' ? 'assigned' : 'new';
    setTogglingStatus(true);
    try {
      await axios.patch(`${API}/companies/${id}/ownership`, { status: newStatus });
      toast.success(`Status set to ${newStatus === 'new' ? 'New' : 'Assigned'}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setTogglingStatus(false);
    }
  };

  const fetchUsers = async () => {
    if (usersList.length > 0 || loadingUsers) return;
    setLoadingUsers(true);
    try {
      const r = await axios.get(`${API}/users/active-basic`);
      setUsersList(r.data || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleTransferOwner = async (userId, userName) => {
    setTransferringOwner(true);
    try {
      await axios.patch(`${API}/companies/${id}/ownership`, { owner_id: userId, owner_name: userName, status: 'assigned' });
      toast.success(`Ownership transferred to ${userName}`);
      setOwnerPopoverOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to transfer ownership');
    } finally {
      setTransferringOwner(false);
    }
  };

  const handleUnassignOwner = async () => {
    setTransferringOwner(true);
    try {
      await axios.patch(`${API}/companies/${id}/ownership`, { owner_id: null, owner_name: null, status: 'new' });
      toast.success('Owner unassigned, status reset to New');
      setOwnerPopoverOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unassign owner');
    } finally {
      setTransferringOwner(false);
    }
  };

  // Notes icon state
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const openNotePopover = () => {
    setNoteText(company?.company_notes || '');
    setNoteOpen(true);
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await axios.patch(`${API}/companies/${id}/notes`, { notes: noteText });
      toast.success('Note saved');
      setNoteOpen(false);
      loadData();
    } catch {
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/companies')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {company.name}
            </h1>
            {/* Website URL */}
            {websiteEditor.isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  type="url"
                  value={websiteEditor.editedWebsite}
                  onChange={(e) => websiteEditor.setEditedWebsite(e.target.value)}
                  placeholder="https://example.com"
                  className="h-8 w-64 text-sm"
                  data-testid="edit-website-input"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-green-600 hover:text-green-700"
                  onClick={websiteEditor.save}
                  disabled={websiteEditor.saving}
                >
                  {websiteEditor.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-400 hover:text-slate-600"
                  onClick={websiteEditor.cancelEditing}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                {company.website ? (
                  <span
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer"
                    data-testid="company-website-link"
                    onClick={() => window.open(company.website.startsWith('http') ? company.website : `https://${company.website}`, '_blank')}
                    onMouseEnter={(e) => {
                      clearTimeout(webPreviewTimerRef.current);
                      const rect = e.currentTarget.getBoundingClientRect();
                      webPreviewTimerRef.current = setTimeout(() => {
                        const url = company.website.startsWith('http') ? company.website : `https://${company.website}`;
                        setWebPreview({ url, rect });
                      }, 400);
                    }}
                    onMouseLeave={() => {
                      clearTimeout(webPreviewTimerRef.current);
                      webPreviewTimerRef.current = setTimeout(() => setWebPreview(null), 300);
                    }}
                  >
                    <Globe className="w-4 h-4" />
                    <span className="underline">{company.website}</span>
                    <ExternalLink className="w-3 h-3" />
                  </span>
                ) : (
                  <span className="text-sm text-slate-400 flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    No website
                  </span>
                )}
                {canCommunicate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-slate-400 hover:text-slate-600"
                    onClick={websiteEditor.startEditing}
                    data-testid="edit-website-btn"
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                )}
                <span className="text-slate-300">|</span>
                {/* Status badge — admin: clickable new↔assigned toggle */}
                {isAdmin ? (
                  <button
                    onClick={handleToggleStatus}
                    disabled={togglingStatus}
                    title={company.status === 'new' ? 'Click to mark Assigned' : 'Click to mark New'}
                    className="cursor-pointer hover:opacity-75 transition-opacity disabled:opacity-50"
                  >
                    {togglingStatus
                      ? <Badge className="bg-slate-100 text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /></Badge>
                      : getStatusBadge(company.status)
                    }
                  </button>
                ) : getStatusBadge(company.status)}
                {/* Owner area — admin: popover for transfer/unassign; non-admin: static display or claim */}
                {isAdmin ? (
                  <Popover open={ownerPopoverOpen} onOpenChange={(v) => { setOwnerPopoverOpen(v); if (v) { fetchUsers(); setUserSearch(''); } }}>
                    <PopoverTrigger asChild>
                      <button
                        className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors cursor-pointer ${
                          company.owner_id === currentUser?.id
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : company.owner_id
                            ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            : 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100'
                        }`}
                      >
                        {company.owner_id === currentUser?.id
                          ? 'Mine ▾'
                          : company.owner_id
                          ? `${company.owner_name || 'Assigned'} ▾`
                          : '+ Assign Owner'}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-3" align="start">
                      <p className="text-sm font-semibold mb-2 text-slate-800">Transfer Ownership</p>
                      {company.owner_id && (
                        <button
                          onClick={handleUnassignOwner}
                          disabled={transferringOwner}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors flex items-center gap-2 mb-2 disabled:opacity-50"
                        >
                          <UserX className="h-4 w-4 flex-shrink-0" />
                          Unassign (reset to New)
                        </button>
                      )}
                      <div className="relative mb-2">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <Input
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          placeholder="Search users..."
                          className="pl-7 h-8 text-sm"
                        />
                      </div>
                      {loadingUsers ? (
                        <div className="flex items-center justify-center py-4 text-slate-400">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading...
                        </div>
                      ) : (
                        <div className="max-h-48 overflow-y-auto space-y-0.5">
                          {usersList
                            .filter(u => !userSearch || (u.full_name || '').toLowerCase().includes(userSearch.toLowerCase()))
                            .map(u => (
                              <button
                                key={u.id}
                                onClick={() => handleTransferOwner(u.id, u.full_name)}
                                disabled={transferringOwner || u.id === company.owner_id}
                                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${
                                  u.id === company.owner_id
                                    ? 'bg-green-50 text-green-700 cursor-default'
                                    : 'hover:bg-slate-100 text-slate-700 disabled:opacity-50'
                                }`}
                              >
                                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-medium text-indigo-700 flex-shrink-0">
                                  {(u.full_name || '?').charAt(0).toUpperCase()}
                                </div>
                                <span className="truncate flex-1">{u.full_name || u.email}</span>
                                {u.id === company.owner_id && <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />}
                              </button>
                            ))}
                          {usersList.filter(u => !userSearch || (u.full_name || '').toLowerCase().includes(userSearch.toLowerCase())).length === 0 && (
                            <p className="text-sm text-slate-400 text-center py-3">No users found</p>
                          )}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                ) : (
                  company.owner_id === currentUser?.id ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Mine</span>
                  ) : company.owner_id ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{company.owner_name || 'Assigned'}</span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={handleClaimCompany} disabled={claiming} className="h-6 text-xs px-2">
                      <UserCheck className="h-3.5 w-3.5 mr-1" />
                      {claiming ? 'Claiming...' : 'Claim Ownership'}
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 text-slate-500 text-sm mt-1 flex-wrap">
            <span className="flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              {company.locations?.length || 0} locations
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {filteredLeads.length} leads{getSelectedLocationName() && ` in ${getSelectedLocationName()}`}
            </span>
            {/* Notes icon */}
            <Popover open={noteOpen} onOpenChange={setNoteOpen}>
              <PopoverTrigger asChild>
                <button
                  onClick={openNotePopover}
                  title={company.company_notes ? 'View / edit note' : 'Add note'}
                  className={`flex items-center gap-1 transition-colors ${
                    company.company_notes
                      ? 'text-green-500 hover:text-green-600'
                      : 'text-slate-400 hover:text-green-500'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  {company.company_notes && (
                    <span className="text-xs font-medium text-green-600">Notes</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-4" align="start">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-500" />
                  {company.company_notes ? 'Edit Note' : 'Add Note'} — {company.name}
                </p>
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add notes about this company..."
                  className="min-h-[120px] resize-none text-sm mb-3"
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setNoteOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={saveNote} disabled={savingNote}>
                    {savingNote && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    Save Note
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            {company?.engagement_band && (
              <ERSBadge score={company.engagement_score} band={company.engagement_band} />
            )}
            <ResearchQuickButton companyId={id} onResearched={loadData} />
          </div>
        </div>
      </div>

      {/* Location filter notification */}
      {selectedLocationId && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2 flex items-center justify-between">
          <span className="text-indigo-700 text-sm">
            Showing leads and communications for <strong>{getSelectedLocationName()}</strong>
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleLocationClick(selectedLocationId)}
            className="text-indigo-600 hover:text-indigo-800"
          >
            Clear filter
          </Button>
        </div>
      )}

      {!canCommunicate && (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm flex items-center gap-2">
          <span className="font-medium">View only</span> — editing is available only to the company owner.
          {!company?.owner_id && (
            <button onClick={handleClaimCompany} disabled={claiming} className="ml-auto text-xs px-2 py-0.5 rounded bg-amber-200 hover:bg-amber-300 font-medium transition-colors">
              {claiming ? 'Claiming...' : 'Claim to get access'}
            </button>
          )}
        </div>
      )}

      <Tabs defaultValue="overview" className="w-full">
        <div className="flex items-center mb-4 gap-2">
          {/* Prev / Next company navigation — left of tabs */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              disabled={!prevId}
              onClick={() => prevId && navigate(`/admin/companies/${prevId}`)}
              title="Previous company"
              className={`flex items-center justify-center h-8 w-8 rounded-md border text-sm font-medium transition-colors
                ${prevId ? 'border-gray-300 text-gray-700 hover:bg-gray-100 cursor-pointer' : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-500 w-16 text-center tabular-nums select-none">
              {neighborIds.length > 0 && currentIdx >= 0 ? `${currentIdx + 1} / ${neighborIds.length}` : '— / —'}
            </span>
            <button
              disabled={!nextId}
              onClick={() => nextId && navigate(`/admin/companies/${nextId}`)}
              title="Next company"
              className={`flex items-center justify-center h-8 w-8 rounded-md border text-sm font-medium transition-colors
                ${nextId ? 'border-gray-300 text-gray-700 hover:bg-gray-100 cursor-pointer' : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {/* Company search */}
            <div className="relative ml-1">
              <div className="flex items-center border rounded-md h-8 px-2 bg-white">
                <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <input
                  type="text"
                  value={companySearchQuery}
                  onChange={(e) => { setCompanySearchQuery(e.target.value); setCompanySearchOpen(true); }}
                  onFocus={() => { if (companySearchQuery.trim()) setCompanySearchOpen(true); }}
                  onBlur={() => setTimeout(() => setCompanySearchOpen(false), 200)}
                  placeholder="Search company..."
                  className="text-xs border-0 outline-none bg-transparent w-36 ml-1"
                />
                {companySearchQuery && (
                  <button onClick={() => { setCompanySearchQuery(''); setCompanySearchOpen(false); }} className="text-gray-400 hover:text-gray-600">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              {companySearchOpen && companySearchResults.length > 0 && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
                  {companySearchResults.map(c => (
                    <button
                      key={c.id}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 ${c.id === id ? 'bg-indigo-50 font-medium text-indigo-700' : 'text-gray-700'}`}
                      onMouseDown={(e) => { e.preventDefault(); navigate(`/admin/companies/${c.id}`); setCompanySearchQuery(''); setCompanySearchOpen(false); }}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger
              value="intelligence"
              className={`flex items-center gap-1.5 ${company?.research_status === 'complete' ? 'data-[state=inactive]:bg-green-100 data-[state=inactive]:text-green-800 data-[state=active]:bg-white data-[state=active]:text-green-700' : ''}`}
            >
              <Search className="w-3.5 h-3.5" />
              Intelligence
              {company?.engagement_band && company.engagement_band !== 'dormant' && (
                <ERSBadge score={company.engagement_score} band={company.engagement_band} showScore={false} />
              )}
            </TabsTrigger>
            <TabsTrigger
              value="decision-makers"
              className={`flex items-center gap-1.5 ${company?.research_status === 'complete' ? 'data-[state=inactive]:bg-green-100 data-[state=inactive]:text-green-800 data-[state=active]:bg-white data-[state=active]:text-green-700' : ''}`}
            >
              <Users className="w-3.5 h-3.5" />
              Decision Makers
            </TabsTrigger>
            <TabsTrigger value="strategy" className="flex items-center gap-1.5">
              Contact Strategy
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Locations */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-indigo-500" />
                Locations
              </CardTitle>
              <Button size="sm" variant="outline" onClick={locationEditor.openAddLocation} data-testid="add-location-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {company.locations?.length > 0 ? (
              company.locations.map((loc, idx) => (
                <div 
                  key={loc.id || idx}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedLocationId === loc.id 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-slate-200 hover:border-indigo-300'
                  }`}
                  onClick={() => handleLocationClick(loc.id)}
                  data-testid={`location-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{loc.name}</p>
                      {loc.address && <p className="text-sm text-slate-500">{loc.address}</p>}
                      {(loc.city || loc.state || loc.zip) && (
                        <p className="text-sm text-slate-500">
                          {[loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {loc.phone && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          {formatPhone(loc.phone)}
                        </p>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 -mt-1 -mr-1"
                      onClick={(e) => locationEditor.openEditLocation(loc, idx, e)}
                    >
                      <Edit className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 py-4 text-center">No locations yet</p>
            )}
          </CardContent>
        </Card>

        {/* Tech Stack & Revenue Section */}
        <Card className={`lg:col-span-2 ${company?.ai_voice_using && !company.ai_voice_using.toLowerCase().includes('jobtalk') ? 'bg-yellow-50 border-yellow-300' : !company?.ai_voice_using ? 'bg-green-50 border-green-200' : ''}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Company Details</CardTitle>
              {!techStackEditor.isEditing ? (
                <Button size="sm" variant="outline" onClick={techStackEditor.startEditing} data-testid="edit-tech-stack-btn">
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={techStackEditor.cancelEditing}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={techStackEditor.save} disabled={techStackEditor.saving} data-testid="save-tech-stack-btn">
                    {techStackEditor.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {techStackEditor.isEditing ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Company Type</Label>
                  <Select value={techStackEditor.editedCompanyType} onValueChange={techStackEditor.setEditedCompanyType}>
                    <SelectTrigger className="mt-1" data-testid="edit-company-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">ATS Used</Label>
                  <Input
                    value={techStackEditor.editedAtsUsed}
                    onChange={(e) => techStackEditor.setEditedAtsUsed(e.target.value)}
                    placeholder="ATS"
                    className="mt-1"
                    list="ats-options"
                    data-testid="edit-ats-used"
                  />
                  <datalist id="ats-options">
                    {ATS_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">AI Voice Tool</Label>
                  <Input
                    value={techStackEditor.editedAiVoiceUsing}
                    onChange={(e) => techStackEditor.setEditedAiVoiceUsing(e.target.value)}
                    placeholder="AI Voice"
                    className="mt-1"
                    list="ai-voice-options"
                    data-testid="edit-ai-voice"
                  />
                  <datalist id="ai-voice-options">
                    {AI_VOICE_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Other AI Tools</Label>
                  <Input
                    value={techStackEditor.editedOtherAiTools}
                    onChange={(e) => techStackEditor.setEditedOtherAiTools(e.target.value)}
                    placeholder="Other tools..."
                    className="mt-1"
                    data-testid="edit-other-ai-tools"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Revenue (e.g., 50M, 1.5B)</Label>
                  <Input
                    value={techStackEditor.editedTotalRevenue}
                    onChange={(e) => techStackEditor.setEditedTotalRevenue(e.target.value)}
                    placeholder="50M, 1.5B..."
                    className="mt-1"
                    data-testid="edit-total-revenue"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Jobs (e.g., 500, 10K)</Label>
                  <Input
                    value={techStackEditor.editedTotalJobs}
                    onChange={(e) => techStackEditor.setEditedTotalJobs(e.target.value)}
                    placeholder="500, 10K..."
                    className="mt-1"
                    data-testid="edit-total-jobs"
                  />
                </div>
                <div className="col-span-2 md:col-span-3">
                  <Label className="text-xs text-slate-500">Company Notes</Label>
                  <Textarea
                    value={techStackEditor.editedCompanyNotes}
                    onChange={(e) => techStackEditor.setEditedCompanyNotes(e.target.value)}
                    placeholder="Internal notes about the company..."
                    className="mt-1"
                    rows={3}
                    data-testid="edit-company-notes"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {company.company_type && (
                  <Badge variant="outline" className="bg-slate-50">
                    Type: {COMPANY_TYPE_OPTIONS.find(o => o.value === company.company_type)?.label || company.company_type}
                  </Badge>
                )}
                {company.ats_used && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">ATS: {company.ats_used}</Badge>
                )}
                {company.ai_voice_using && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700">AI Voice: {company.ai_voice_using}</Badge>
                )}
                {company.other_ai_tools && (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Tools: {company.other_ai_tools}</Badge>
                )}
                {company.total_revenue && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Revenue: {formatShorthand(company.total_revenue)}</Badge>
                )}
                {company.total_jobs && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700">Jobs: {formatShorthand(company.total_jobs)}</Badge>
                )}
                {!company.company_type && !company.ats_used && !company.ai_voice_using && !company.other_ai_tools && !company.total_revenue && !company.total_jobs && !company.company_notes && (
                  <span className="text-sm text-slate-400">No details added yet</span>
                )}
              </div>
            )}
            {!techStackEditor.isEditing && company.company_notes && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-semibold text-amber-800 mb-1">Notes:</p>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{company.company_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Leads and Communications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                Leads
                {getSelectedLocationName() && (
                  <Badge variant="outline" className="ml-2 text-xs">{getSelectedLocationName()}</Badge>
                )}
              </CardTitle>
              <Button size="sm" variant="outline" onClick={leadManager.openAddLeadDialog} data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Lead
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredLeads.length > 0 ? (
                filteredLeads.map(lead => (
                  <div
                    key={lead.id}
                    className="p-3 rounded-lg border border-slate-200 hover:border-indigo-300 cursor-pointer transition-all"
                    onClick={() => navigate(`/admin/leads/${lead.id}`)}
                    data-testid={`lead-${lead.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">
                          {lead.first_name} {lead.last_name || ''}
                        </p>
                        {lead.title && <p className="text-sm text-slate-500">{lead.title}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          {lead.work_email && (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {lead.work_email}
                            </span>
                          )}
                          {lead.owner_name ? (
                            <span className="text-xs text-indigo-500 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {lead.owner_name}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Unassigned
                            </span>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(lead.status)}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 py-8 text-center">
                  {selectedLocationId ? 'No leads at this location' : 'No leads yet'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Communications */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-500" />
              Recent Communications
              {getSelectedLocationName() && (
                <Badge variant="outline" className="ml-2 text-xs">{getSelectedLocationName()}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredCommunications.length > 0 ? (
                filteredCommunications.slice(0, 10).map((comm, idx) => (
                  <div
                    key={comm.id || idx}
                    className="flex gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all"
                    onClick={() => comm.lead_id && navigate(`/admin/leads/${comm.lead_id}`)}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getCommBgColor(comm.type)}`}>
                      {getCommIcon(comm.type) || <MessageSquare className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-900 text-sm">{comm.lead_name}</p>
                        <span className="text-xs text-slate-400">
                          {comm.created_at && format(parseISO(comm.created_at), 'MM-dd h:mm aa')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{comm.content || comm.subject || 'No content'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs capitalize">{comm.type}</Badge>
                        {comm.user_name && (
                          <span className="text-xs text-slate-500">{comm.user_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 py-8 text-center">
                  {selectedLocationId ? 'No communications at this location' : 'No communications yet'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

        </TabsContent>

        <TabsContent value="intelligence">
          <CompanyResearchTab
            companyId={id}
            companyName={company?.name}
            company={company}
          />
        </TabsContent>

        <TabsContent value="decision-makers">
          <CompanyDecisionMakersTab
            companyId={id}
            companyName={company?.name}
          />
        </TabsContent>

        <TabsContent value="strategy">
          <CompanyContactStrategy
            companyId={id}
            company={company}
            onAddAsLead={(dm) => {
              // Re-use existing add-as-lead logic from decision makers tab
              axios.post(`/api/companies/${id}/research/decision-makers/${dm.id}/add-as-lead`, {}, { withCredentials: true })
                .then(() => { toast.success(`${dm.name} added as lead`); })
                .catch(err => toast.error(err.response?.data?.detail || 'Failed to add as lead'));
            }}
            onResearchDMs={() => {
              axios.post(`/api/companies/${id}/research`, {}, { withCredentials: true })
                .then(() => toast.success('Research started'))
                .catch(err => toast.error(err.response?.data?.detail || 'Research failed'));
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Location Dialog */}
      <Dialog open={locationEditor.editLocationOpen} onOpenChange={locationEditor.setEditLocationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {locationEditor.editingLocationIndex === -1 ? 'Add Location' : 'Edit Location'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Location Name *</Label>
              <Input
                value={locationEditor.editingLocation?.name || ''}
                onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, name: e.target.value})}
                placeholder="Headquarters, Branch Office, etc."
                data-testid="location-name-input"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={locationEditor.editingLocation?.address || ''}
                onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, address: e.target.value})}
                placeholder="Street address"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>City</Label>
                <Input
                  value={locationEditor.editingLocation?.city || ''}
                  onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, city: e.target.value})}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={locationEditor.editingLocation?.state || ''}
                  onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, state: e.target.value})}
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  value={locationEditor.editingLocation?.zip || ''}
                  onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, zip: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={locationEditor.editingLocation?.phone || ''}
                onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            {locationEditor.editingLocationIndex !== -1 && (
              <Button 
                variant="destructive" 
                onClick={locationEditor.handleDeleteLocation}
                disabled={locationEditor.saving}
              >
                Delete
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => locationEditor.setEditLocationOpen(false)}>
                Cancel
              </Button>
              <Button onClick={locationEditor.handleSaveLocation} disabled={locationEditor.saving} data-testid="save-location-btn">
                {locationEditor.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                {locationEditor.editingLocationIndex === -1 ? 'Add' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={leadManager.addLeadOpen} onOpenChange={leadManager.setAddLeadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Lead to Company</DialogTitle>
          </DialogHeader>

          {/* Tab switcher */}
          <div className="flex border-b mb-2">
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${leadManager.addLeadMode === 'new' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => leadManager.setAddLeadMode('new')}
            >
              Create New
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${leadManager.addLeadMode === 'existing' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => {
                leadManager.setAddLeadMode('existing');
                if (leadManager.allLeads.length === 0) leadManager.fetchAllLeads();
              }}
            >
              Choose Existing
            </button>
          </div>

          {leadManager.addLeadMode === 'new' ? (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={leadManager.addLeadForm.first_name}
                    onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, first_name: e.target.value})}
                    placeholder="John"
                    data-testid="lead-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={leadManager.addLeadForm.last_name}
                    onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, last_name: e.target.value})}
                    placeholder="Doe"
                    data-testid="lead-last-name"
                  />
                </div>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={leadManager.addLeadForm.title}
                  onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, title: e.target.value})}
                  placeholder="VP of Sales"
                />
              </div>
              <div>
                <Label>Work Email</Label>
                <Input
                  type="email"
                  value={leadManager.addLeadForm.work_email}
                  onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, work_email: e.target.value})}
                  placeholder="john@company.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mobile Phone</Label>
                  <Input
                    value={leadManager.addLeadForm.mobile_phone}
                    onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, mobile_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label>Work Direct</Label>
                  <Input
                    value={leadManager.addLeadForm.work_direct_phone}
                    onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, work_direct_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={leadManager.addLeadForm.status}
                    onValueChange={(val) => leadManager.setAddLeadForm({...leadManager.addLeadForm, status: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Select
                    value={leadManager.addLeadForm.location_id}
                    onValueChange={(val) => leadManager.setAddLeadForm({...leadManager.addLeadForm, location_id: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {company.locations?.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-2 space-y-3">
              <p className="text-sm text-slate-500">Search for an existing lead and link them to this company.</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Search by name, email, title, or company..."
                  value={leadManager.existingLeadsSearch}
                  onChange={(e) => leadManager.setExistingLeadsSearch(e.target.value)}
                />
              </div>
              {leadManager.loadingLeads ? (
                <div className="flex items-center justify-center py-8 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leads...
                </div>
              ) : (
                <div className="border rounded-md overflow-y-auto max-h-64">
                  {leadManager.filteredExistingLeads.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">
                      {leadManager.existingLeadsSearch ? 'No leads match your search.' : 'No leads available to link.'}
                    </p>
                  ) : (
                    leadManager.filteredExistingLeads.map(lead => {
                      const isSelected = leadManager.selectedExistingLead?.id === lead.id;
                      return (
                        <button
                          key={lead.id}
                          className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors border-b last:border-b-0 ${isSelected ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                          onClick={() => leadManager.setSelectedExistingLead(isSelected ? null : lead)}
                        >
                          <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800">
                              {lead.first_name} {lead.last_name}
                            </p>
                            {lead.title && <p className="text-xs text-slate-500">{lead.title}</p>}
                            {lead.company_name && (
                              <p className="text-xs text-slate-400 truncate">
                                Currently at: {lead.company_name}
                              </p>
                            )}
                            {lead.work_email && <p className="text-xs text-slate-400">{lead.work_email}</p>}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              {leadManager.selectedExistingLead && (
                <p className="text-xs text-blue-600 font-medium">
                  <UserCheck className="w-3.5 h-3.5 inline mr-1" />
                  Selected: {leadManager.selectedExistingLead.first_name} {leadManager.selectedExistingLead.last_name}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => leadManager.setAddLeadOpen(false)}>
              Cancel
            </Button>
            {leadManager.addLeadMode === 'new' ? (
              <Button onClick={leadManager.handleAddLead} disabled={leadManager.saving} data-testid="save-lead-btn">
                {leadManager.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Add Lead
              </Button>
            ) : (
              <Button
                onClick={leadManager.handleLinkExistingLead}
                disabled={leadManager.linkingSaving || !leadManager.selectedExistingLead}
              >
                {leadManager.linkingSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserCheck className="w-4 h-4 mr-1" />}
                Link to Company
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Website hover preview popup */}
      {webPreview && (() => {
        const POPUP_W = 700;
        const POPUP_H = 440;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const left = Math.min(webPreview.rect.left, vw - POPUP_W - 12);
        const top = webPreview.rect.bottom + 6 + POPUP_H > vh
          ? webPreview.rect.top - POPUP_H - 6
          : webPreview.rect.bottom + 6;
        return (
          <div
            style={{ position: 'fixed', left, top, width: POPUP_W, height: POPUP_H, zIndex: 9999 }}
            onMouseEnter={() => clearTimeout(webPreviewTimerRef.current)}
            onMouseLeave={() => { clearTimeout(webPreviewTimerRef.current); webPreviewTimerRef.current = setTimeout(() => setWebPreview(null), 200); }}
            className="rounded-xl shadow-2xl border border-slate-200 overflow-hidden bg-white flex flex-col"
          >
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 border-b border-slate-200 shrink-0">
              <Globe className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-xs text-slate-500 truncate flex-1">{webPreview.url}</span>
              <a href={webPreview.url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-600">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button onClick={() => setWebPreview(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <iframe
              src={webPreview.url}
              title="Website preview"
              className="flex-1 w-full"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          </div>
        );
      })()}
    </div>
  );
}
